/**
 * Echo Hunt -- the judge prompt (P10, §9.1 + §9.5).
 *
 * VISIBILITY-MATRIX ROW (§16.0): "Loop judge / held-out judge".
 *  - knows real/fake?      NO. No labels are passed, and there is no parameter through which one could
 *                          be -- the same structural guarantee the weaver prompt has.
 *  - sees world/character?  NO (invariant #4). A judge that knew the world could decide by "does this
 *                          fit the world's lore", which is not what the game trains.
 *  - sees other bullets?    YES, the whole story. Judging a bullet in isolation would remove exactly the
 *                          context the user has when hunting.
 *  - sees corpus?           NO, and this one is load-bearing (§9.2): a judge with corpus access would
 *                          flag bullets precisely BECAUSE they resemble the user's dreams, so the loop
 *                          would strip out the very familiarity cues the game is meant to train on.
 *
 * ## What this prompt must NOT say (§9.5)
 *
 * Nothing about how the pipeline works. §9.5 defers the "pipeline-aware judge" to v2 precisely because a
 * judge told how bullets are made could exploit a structural artefact (e.g. "reshaped bullets contain an
 * odd noun"), and the loop would then repair away a tell that only exists for a reader who knows the
 * instructions. So this prompt describes the TASK and nothing about where the text came from.
 *
 * ## Why a float rather than a top-X bucket
 *
 * §9.2 prefers AUC over the top-X miscategorization %, because AUC is independent of X and finer-grained
 * (the % has a ceiling of `2X(1-X)`). Asking for a probability per bullet gives both numbers from one
 * call, and lets the held-out judge be compared to the loop judge directly.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §9, §2 (#1, #2), §16.0, §16.2 P10.
 */
import type {JSONSchemaLike} from "../LLM.js";

/** What the judge is looking for. Deliberately phrased as "one person's remembered experience" rather
 * than "a dream", because a judge told "find the dreams" reaches for generic dream imagery -- which is
 * exactly the cue §9.2's Y band is meant to decide about, not to assume. */
export const JUDGE_RULES = [
	"Each entry is either (a) something a real person actually experienced and wrote down later, or (b) something written for this story.",
	"Both kinds were rewritten for this installment, so surface polish says nothing.",
	"A probability near 1 means you are confident it is a real remembered experience; near 0 means you are confident it was written.",
	"Use the middle of the range freely. A calibrated 0.6 is more useful than a confident 0.95.",
	"Judge each entry on its own, but let the rest of the installment inform it: a body of events written together tends to cohere in ways separate recollections do not.",
	"Do not try to find a rule that separates the two kinds. If a rule seems to work on one entry, check whether it holds on the others before relying on it.",
].map(a=>`- ${a}`).join("\n");

/** Observed tells, stated as things to WEIGHT rather than as a checklist to apply mechanically. §9.2's
 * whole purpose is to keep the judge near chance on a well-made round, so an over-eager checklist would
 * make every round look too easy and the loop would reject everything. */
export const JUDGE_TELLS = [
	"Written entries often resolve: a feeling is named, a scene reaches a tidy point, an event is given a meaning.",
	"Written entries often contain an object that exists only to be noticed.",
	"Remembered entries often contain something that is never explained, and the writer does not seem to mind.",
	"Remembered entries often contain a detail with no narrative role at all -- a number, a piece of furniture, a minor annoyance.",
].map(a=>`- ${a}`).join("\n");

export function Judge_SystemPrompt(): string {
	return [
		"You are an ordinary careful reader of a short piece of prose fiction.",
		"You are told that some of its sentences each record something a real person experienced, and that others were written.",
		"Your job is to say, for each sentence, how likely it is to be a record of a real experience.",
		"You have no other information, and you should not speculate about who wrote this or why.",
		"Reply with JSON only. No prose, no code fences, no explanation.",
	].join("\n");
}

export type Judge_UserPromptOptions = {
	/** the installment, in story order, as `{id, text}` -- an opaque id and the text, nothing else */
	bullets: {id: string, text: string}[];
	/** on a retry: the problems found in the previous reply */
	fixNotes?: string|n;
};

/** The judge prompt. Every bullet is a `{"id": ..., "text": ...}` pair, so the reply can be matched by
 * id and a reordered/omitted entry is detectable (§9.1). */
export function Judge_UserPrompt(opts: Judge_UserPromptOptions): string {
	const {bullets, fixNotes} = opts;

	const parts = [
		"## Task",
		"Below is a short installment of a story, one sentence per line, each with an id.",
		"Some of these sentences each describe something that really happened to a person and was later written down.",
		"The rest were written for this installment.",
		"For each id, give the probability that it describes a real remembered experience, from 0 to 1.",
		"",
		"## What the two kinds look like",
		JUDGE_RULES,
		"",
		"## Common tells, to weigh rather than to apply",
		JUDGE_TELLS,
		"",
		"## The installment",
		JSON.stringify(bullets.map(bullet=>({id: bullet.id, text: bullet.text})), null, 2),
		"",
		"## Output",
		"Reply with ONE JSON object:",
		`{"scores": [{"id": "<id>", "p": 0.72}, ...]}`,
		"",
		"Hard requirements:",
		"- One entry per id above, exactly once. Same number of entries out as ids in.",
		"- `p` is a number from 0 to 1, not a percentage and not a label.",
	];

	if (fixNotes != null && fixNotes.trim().length > 0) {
		parts.push(
			"",
			"## Fix these problems from your previous attempt",
			fixNotes.trim(),
			"",
			"Reply with the FULL corrected object again (every id, not only the ones listed).",
		);
	}

	return parts.join("\n");
}

/** The JSON shape `CallLLM` validates each attempt against. Loose on purpose, like the weaver's: the
 * completeness check (same id set, same count) lives in `Judge.ts`, where a failure can be explained to
 * the model in plain language instead of as a schema error. */
export function Judge_JSONSchema(): JSONSchemaLike {
	return {
		type: "object",
		required: ["scores"],
		properties: {
			scores: {
				type: "array",
				minItems: 1,
				items: {
					type: "object",
					required: ["id", "p"],
					properties: {
						id: {type: "string"},
						p: {type: "number"},
					},
				},
			},
		},
	};
}
